import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, AlertTriangle } from 'lucide-react';
import { applications } from '../api';
import type { VorlagenBefund } from '../api';
import CodeFeld from './CodeFeld';
import StatusBadge from './StatusBadge';
import type { TemplateStatus, ComposeTemplate } from '../types';

/*
 * Die Compose-Vorlagen einer Application — Liste und „Vorlage anlegen".
 *
 * Stand bis 10.10.2026 als Reiter auf der Produktseite. Seit Stufe 2 gehoeren
 * Vorlagen der Application (docs/KONZEPT-PRODUKT-UND-APPVORLAGE.md); Formular,
 * Pruefer und Anzeige sind unveraendert hierher umgezogen.
 */

/**
 * Eine neue Vorlage traegt alles, was die alte trug.
 *
 * Vorher nahm der Dialog Version, compose und env — und liess den Rest fallen:
 * `generatedSecrets`, `tenantsSupported`, `adminEmailField`, die Modullisten.
 * Eine so angelegte ojfm-Vorlage war nicht ausrollbar, weil die
 * Pflichtgeheimnisse fehlten und `${…:?required}` den Deploy abbricht. Und das
 * faellt erst beim Ausrollen auf, nicht beim Anlegen.
 */
interface TemplateForm {
  version: string;
  composeTemplate: string;
  envTemplate: string;
  status: TemplateStatus;
  generatedSecrets: string[];
  adminEmailField: string | null;
  adminSecretKey: string | null;
  adminAppliesOnDeploy: boolean;
  customerAdminSupported: boolean;
  tenantsSupported: boolean;
  usersSupported: boolean;
  tenantsSource: Record<string, unknown> | null;
  reservedModules: string[];
  availableModules: string[];
  migrationStrategy: string | null;
  healthcheckConfig: Record<string, unknown> | null;
  backupConfig: Record<string, unknown> | null;
}

const DEFAULT_TEMPLATE_FORM: TemplateForm = {
  version: '',
  composeTemplate: '',
  envTemplate: '',
  status: 'ACTIVE',
  generatedSecrets: [],
  adminEmailField: null,
  adminSecretKey: null,
  adminAppliesOnDeploy: false,
  customerAdminSupported: false,
  tenantsSupported: false,
  usersSupported: false,
  tenantsSource: null,
  reservedModules: [],
  availableModules: [],
  migrationStrategy: null,
  healthcheckConfig: null,
  backupConfig: null,
};

/** Die naechste Patch-Nummer. Aus 3.1.4 wird 3.1.5, aus allem anderen nichts. */
function naechsteVersion(version: string): string {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(version.trim());
  return m ? `${m[1]}.${m[2]}.${Number(m[3]) + 1}` : '';
}

/**
 * Uebernimmt eine bestehende Vorlage ins Formular.
 *
 * Die Version wird hochgezaehlt statt uebernommen: derselbe Wert wuerde die
 * alte Zeile ueberschreiben, und dann liefe eine Instanz unter einer Nummer,
 * hinter der etwas anderes steht als gestern.
 */
function alsFormular(v: ComposeTemplate): TemplateForm {
  return {
    version: naechsteVersion(v.version),
    composeTemplate: v.composeTemplate,
    envTemplate: v.envTemplate,
    status: 'ACTIVE',
    generatedSecrets: v.generatedSecrets ?? [],
    adminEmailField: v.adminEmailField ?? null,
    adminSecretKey: v.adminSecretKey ?? null,
    adminAppliesOnDeploy: Boolean(v.adminAppliesOnDeploy),
    customerAdminSupported: Boolean(v.customerAdminSupported),
    tenantsSupported: Boolean(v.tenantsSupported),
    usersSupported: Boolean(v.usersSupported),
    tenantsSource: v.tenantsSource ?? null,
    reservedModules: v.reservedModules ?? [],
    availableModules: v.availableModules ?? [],
    migrationStrategy: v.migrationStrategy ?? null,
    healthcheckConfig: v.healthcheckConfig ?? null,
    backupConfig: v.backupConfig ?? null,
  };
}

export default function VorlagenBereich({ applicationId }: { applicationId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  /** Aus welcher Vorlage das Formular gefuellt wurde — nur zur Anzeige. */
  const [vorbild, setVorbild] = useState<string | null>(null);
  /**
   * Was der Prüfer zur aktuellen Fassung sagt — verzögert mitlaufend, damit
   * der Fehler dort erscheint, wo er entsteht, und nicht im Protokoll eines
   * Deploys auf dem Host eines Kunden.
   */
  const [befund, setBefund] = useState<VorlagenBefund | null>(null);
  const [templateForm, setTemplateForm] = useState<TemplateForm>(DEFAULT_TEMPLATE_FORM);
  const [formError, setFormError] = useState('');

  const { data: templatesList, isLoading: loadingTemplates } = useQuery({
    queryKey: ['applications', applicationId, 'templates'],
    queryFn: () => applications.getTemplates(applicationId),
  });

  useEffect(() => {
    if (!showTemplateModal) return;
    if (!templateForm.composeTemplate.trim()) {
      setBefund(null);
      return;
    }
    const uhr = setTimeout(() => {
      applications
        .pruefeTemplate(applicationId, templateForm.composeTemplate, templateForm.envTemplate)
        .then(setBefund)
        // Ein nicht erreichbarer Prüfer darf das Formular nicht blockieren —
        // dann gilt eben wieder, was der Server beim Speichern sagt.
        .catch(() => setBefund(null));
    }, 500);
    return () => clearTimeout(uhr);
  }, [showTemplateModal, applicationId, templateForm.composeTemplate, templateForm.envTemplate]);

  const createTemplateMutation = useMutation({
    mutationFn: (payload: Partial<typeof templateForm>) => applications.createTemplate(applicationId, payload as Partial<ComposeTemplate>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications', applicationId] });
      qc.invalidateQueries({ queryKey: ['applications'] });
      setShowTemplateModal(false);
      setTemplateForm(DEFAULT_TEMPLATE_FORM);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Vorlage konnte nicht angelegt werden.';
      setFormError(msg);
    },
  });

  return (
    <>
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              onClick={() => {
                // Neueste zuerst — die Liste kommt nach createdAt absteigend.
                const letzte = templatesList?.[0];
                setTemplateForm(letzte ? alsFormular(letzte) : DEFAULT_TEMPLATE_FORM);
                setVorbild(letzte?.version ?? null);
                setShowTemplateModal(true);
                setFormError('');
              }}
              className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg"
            >
              <Plus size={14} /> Add Template
            </button>
          </div>
          {loadingTemplates ? (
            <div className="space-y-2 animate-pulse">
              {[1, 2].map((i) => <div key={i} className="h-16 bg-slate-100 rounded-lg" />)}
            </div>
          ) : templatesList?.length === 0 ? (
            <p className="text-slate-400 italic text-sm">{t('products.noTemplates')}</p>
          ) : (
            <div className="grid gap-3">
              {templatesList?.map((tpl) => (
                <div key={tpl.id} className="bg-surface border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-slate-900">v{tpl.version}</span>
                    <StatusBadge status={tpl.status} type="template" />
                    {tpl.migrationStrategy && (
                      <span className="text-xs text-slate-400">Migration: {tpl.migrationStrategy}</span>
                    )}
                  </div>
                  <details>
                    <summary className="text-xs text-slate-500 cursor-pointer">{t('products.showTemplate')}</summary>
                    <pre className="mt-2 text-xs bg-konsole text-konsole-gruen p-3 rounded overflow-x-auto max-h-48">
                      {tpl.composeTemplate}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface">
              <h2 className="text-base font-semibold">{t('products.addTemplate')}</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFormError('');
                createTemplateMutation.mutate(templateForm);
              }}
              className="px-6 py-5 space-y-4"
            >
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{formError}</div>
              )}
              {/* Woher das Formular kommt. Ohne diesen Satz sieht ein
                  vorbefuelltes Formular aus wie ein leeres, in das jemand
                  versehentlich etwas getippt hat. */}
              <div className={`rounded-lg px-3 py-2 text-xs ${vorbild ? 'bg-sky-50 text-sky-800' : 'bg-slate-50 text-slate-600'}`}>
                {vorbild
                  ? t('templateForm.basedOn', { version: vorbild })
                  : t('templateForm.nothingToCopy')}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                <input
                  required
                  placeholder="1.0.0"
                  value={templateForm.version}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, version: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('products.composeTemplate')}</label>
                <CodeFeld
                  sprache="yaml"
                  zeilen={20}
                  wert={templateForm.composeTemplate}
                  onChange={(w) => setTemplateForm((f) => ({ ...f, composeTemplate: w }))}
                  platzhalter={'services:\n  app:\n    image: …'}
                  marken={(befund?.befunde ?? [])
                    .filter((b) => b.zeile)
                    .map((b) => ({ zeile: b.zeile!, schwere: b.schwere, text: b.text }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('products.envTemplate')}</label>
                <CodeFeld
                  sprache="env"
                  zeilen={12}
                  wert={templateForm.envTemplate}
                  onChange={(w) => setTemplateForm((f) => ({ ...f, envTemplate: w }))}
                  platzhalter={'INSTANCE={{STACK_NAME}}\nDB_PASSWORD={{DB_PASSWORD}}'}
                />
              </div>
              {/* Was der Prüfer sagt.
                  Fehler zuerst — sie verhindern das Speichern. Hinweise
                  darunter: sie können richtig sein, sind aber meistens ein
                  Versehen. */}
              {befund && (
                <div
                  className={`rounded-lg px-3 py-2 text-xs ${
                    befund.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {befund.ok ? (
                    <span>
                      {t('templateCheck.ok', { count: befund.dienste.length })}
                      {befund.befunde.length > 0 && (
                        <span className="mt-1 block text-amber-700">
                          {befund.befunde.map((b) => b.text).join(' · ')}
                        </span>
                      )}
                    </span>
                  ) : (
                    <>
                      <div className="mb-1 font-medium">{t('templateCheck.failed')}</div>
                      <ul className="space-y-0.5">
                        {befund.befunde
                          .filter((b) => b.schwere === 'fehler')
                          .map((b, i) => (
                            <li key={i}>
                              {b.zeile ? (
                                <span className="mr-1 font-mono text-red-600">Z{b.zeile}</span>
                              ) : null}
                              {b.text}
                            </li>
                          ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {/* Was mitgeht, ohne dass man es anfasst.
                  Sichtbar, nicht editierbar: es ist genau die Liste, deren
                  stilles Fehlen die Vorlage unbrauchbar machte — man soll sehen,
                  dass sie da ist. Wer sie aendern will, tut das im Seed-Skript,
                  wo sie zusammen mit der compose.yaml versioniert ist. */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
                <div className="mb-1.5 font-medium text-slate-700">{t('templateForm.carried')}</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <dt>{t('templateForm.secrets')}</dt>
                  <dd className="font-mono">
                    {templateForm.generatedSecrets.length
                      ? templateForm.generatedSecrets.join(', ')
                      : '—'}
                  </dd>
                  <dt>{t('templateForm.adminField')}</dt>
                  <dd className="font-mono">{templateForm.adminEmailField ?? '—'}</dd>
                  <dt>{t('templateForm.tenants')}</dt>
                  <dd className="font-mono">{templateForm.tenantsSupported ? 'ja' : 'nein'}</dd>
                  {/* Steuert, ob an der Instanz der Reiter „App-Zugaenge" erscheint.
                      Wie die uebrigen hier: mitgefuehrt, nicht editierbar — geaendert
                      wird es im Seed-Skript, wo es mit der compose.yaml versioniert
                      ist. Ein Schalter hier waere eine zweite Wahrheit. */}
                  <dt>Nutzerverwaltung</dt>
                  <dd className="font-mono">{templateForm.usersSupported ? 'ja' : 'nein'}</dd>
                  <dt>{t('templateForm.modules')}</dt>
                  <dd className="font-mono">
                    {templateForm.availableModules.length || '—'}
                    {templateForm.reservedModules.length
                      ? ` (${templateForm.reservedModules.length} reserviert)`
                      : ''}
                  </dd>
                </dl>
                {!templateForm.generatedSecrets.length && (
                  <div className="mt-2 flex items-start gap-1.5 text-amber-700">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    <span>{t('templateForm.warnEmpty')}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowTemplateModal(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600">Cancel</button>
                <button type="submit" disabled={createTemplateMutation.isPending || befund?.ok === false} className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60">
                  {createTemplateMutation.isPending ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
